import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import moment, { Moment } from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteIcon from 'src/shared/assets/icons/delete.svg';
import DotsIcon from 'src/shared/assets/icons/dots.svg';
import SplitIcon from 'src/shared/assets/icons/split.svg';
import { Button } from 'src/shared/components/button';
import { FormDatePicker, FormNumberInput, FormSelect } from 'src/shared/components/form-components';
import { FormTooltip } from 'src/shared/components/form-components/form-tooltip';
import { hasValue } from 'src/shared/utils/common';

import { RigOperationSection } from '../../entities/rig-operations/rig-operation-section.entity';
import { RigOperationStage } from '../../entities/rig-operations/rig-operation-stage.entity';
import { RigOperation } from '../../entities/rig-operations/rig-operation.entity';
import { RigOperationsViewModel } from '../../entities/rig-operations/rig-operations-view-model';

import styles from './section-item.module.scss';

type Props = {
  section: RigOperationSection;
  stage: RigOperationStage;
  rigOperation: RigOperation;
  rigOperationsViewModel: RigOperationsViewModel;
  onValueChange(fieldId: string, value: unknown, section: RigOperationSection): void;
  onDeleteSection(): void;
  isFieldBlocked(fieldId: string): boolean;
  getDefaultTime(fieldId: 'startDate' | 'endDate'): Moment;
  getDisabledTime(fieldId: 'startDate' | 'endDate', section?: RigOperationSection): Moment | null;
};

export const SectionItem = observer<Props>(function SectionItem({
  section,
  rigOperation,
  stage,
  rigOperationsViewModel,
  onValueChange,
  onDeleteSection,
  isFieldBlocked,
  getDefaultTime,
  getDisabledTime,
}) {
  const { t } = useTranslation();

  const { attributes, listeners, transform, transition, isDragging, setNodeRef, setActivatorNodeRef } = useSortable({
    id: section.entityId,
  });

  const { sectionTypeOptions, isFormDisabled, validationService, getValidSelectValue } = rigOperationsViewModel;

  const startDate = useMemo(() => {
    if (!hasValue(section.startDate)) {
      return null;
    }

    return moment(section.startDate * 1000);
  }, [section.startDate]);

  const endDate = useMemo(() => {
    if (!hasValue(section.endDate)) {
      return null;
    }

    return moment(section.endDate * 1000);
  }, [section.endDate]);

  const defaultStartDate = getDefaultTime('startDate');
  const defaultEndDate = getDefaultTime('endDate');
  const disabledStartDate = getDisabledTime('startDate');
  const disabledEndDate = getDisabledTime('endDate');

  const handleSplitSections = (): void => {
    // onSplitApproach?.({
    //   sectionId: section.id,
    //   stage,
    //   approach,
    // });
  };

  const dragStyles = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={dragStyles}
      className={clsx(styles.wrapper, isDragging && styles.wrapper__active)}
    >
      <div className={styles.contentWrapper}>
        {!isFormDisabled && (
          <>
            <Button
              variant="default"
              disabled
              className={styles.splitButton}
              icon={<SplitIcon />}
              onClick={handleSplitSections}
            />
            <div ref={setActivatorNodeRef} className={styles.grabIcon__wrapper} {...listeners}>
              <DotsIcon className={styles.grabIcon} />
            </div>
            <Button
              variant="default"
              className={styles.deleteButton}
              icon={<DeleteIcon width={18} height={18} />}
              onClick={onDeleteSection}
            />
          </>
        )}
        <FormTooltip text={section.errors.sectionTypeId}>
          <FormSelect
            value={getValidSelectValue('sectionTypeId', section.sectionTypeId)}
            allowClear={false}
            isDisabled={isFieldBlocked('sectionTypeId')}
            isError={!!section.errors.sectionTypeId}
            className={styles.sectionTypeSelect}
            options={sectionTypeOptions.actual}
            onChange={(value) => onValueChange('sectionTypeId', value, section)}
            onBlur={() => validationService.validateSectionType(rigOperation, stage, section)}
            onFocus={() => section.removeError('sectionTypeId')}
          />
        </FormTooltip>
      </div>
      <div className={styles.fieldsWrapper}>
        <div className={styles.intervalWrapper}>
          <div>
            <p className={styles.fieldLabel}>Дата начала</p>
            <FormTooltip text={section.errors.startDate}>
              <FormDatePicker
                withTime
                value={startDate}
                defaultDate={defaultStartDate}
                defaultTime={defaultStartDate}
                isError={!!section.errors.startDate}
                disabledDate={(date) => date.diff(disabledStartDate) < 0}
                disabled={isFieldBlocked('startDate')}
                placeholder="Выберите дату"
                onChange={(value) => onValueChange('startDate', value, section)}
                onBlur={() => validationService.validateStartDate(rigOperation, stage, section)}
                onFocus={() => section.removeError('startDate')}
              />
            </FormTooltip>
          </div>
          <p className={styles.dash}>—</p>
          <div>
            <p className={styles.fieldLabel}>Дата окончания</p>
            <FormTooltip text={section.errors.endDate}>
              <FormDatePicker
                withTime
                value={endDate}
                defaultDate={defaultEndDate}
                defaultTime={defaultEndDate}
                isError={!!section.errors.endDate}
                disabledDate={(date) => date.diff(disabledEndDate) < 0}
                disabled={isFieldBlocked('endDate')}
                placeholder="Выберите дату"
                onChange={(value) => onValueChange('endDate', value, section)}
                onBlur={() => validationService.validateEndDate(rigOperation, stage, section)}
                onFocus={() => section.removeError('endDate')}
              />
            </FormTooltip>
          </div>
        </div>
        <div>
          <p className={styles.fieldLabel}>Срок</p>
          <FormTooltip text={section.errors.duration}>
            <FormNumberInput
              value={section.duration}
              isDisabled={isFieldBlocked('duration')}
              isError={!!section.errors.duration}
              onChange={(value) => onValueChange('duration', value, section)}
              onBlur={() => validationService.validateDurationField(rigOperation, stage, section)}
              onFocus={() => section.removeError('duration')}
              placeholder="Введите значение"
              unit="Суток"
            />
          </FormTooltip>
        </div>
        <div>
          <p className={styles.fieldLabel}>Проходка</p>
          <FormTooltip text={section.errors.passingValue}>
            <FormNumberInput
              value={section.passingValue}
              isDisabled={isFieldBlocked('passingValue')}
              isError={!!section.errors.passingValue}
              onChange={(value) => onValueChange('passingValue', value, section)}
              onBlur={() => validationService.validatePassingValueField(rigOperation, stage, section)}
              onFocus={() => section.removeError('passingValue')}
              placeholder="Введите значение"
              unit="м"
            />
          </FormTooltip>
        </div>
        <div className={styles.intervalWrapper}>
          <div>
            <p className={styles.fieldLabel}>Интервал</p>
            <FormTooltip text={section.errors.passingValueMin}>
              <FormNumberInput
                value={section.passingValueMin}
                isDisabled={isFieldBlocked('passingValueMin')}
                isError={!!section.errors.passingValueMin}
                onChange={(value) => onValueChange('passingValueMin', value, section)}
                onBlur={() => validationService.validatePassingValueMinField(rigOperation, stage, section)}
                onFocus={() => section.removeError('passingValueMin')}
                prefix="от"
                unit="метров"
              />
            </FormTooltip>
          </div>
          <p className={styles.dash}>—</p>
          <div>
            <FormTooltip text={section.errors.passingValueMax}>
              <FormNumberInput
                value={section.passingValueMax}
                isDisabled={isFieldBlocked('passingValueMax')}
                isError={!!section.errors.passingValueMax}
                onChange={(value) => onValueChange('passingValueMax', value, section)}
                onBlur={() => validationService.validatePassingValueMaxField(rigOperation, stage, section)}
                onFocus={() => section.removeError('passingValueMax')}
                prefix="до"
                unit="метров"
              />
            </FormTooltip>
          </div>
        </div>
      </div>
    </div>
  );
});
